import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [appliedVoucher, setAppliedVoucher] = useState(null);
    const [userPoints, setUserPoints] = useState(0);

    // Load states from localStorage
    useEffect(() => {
        const savedCart = localStorage.getItem('sugarhut_cart');
        if (savedCart) setCart(JSON.parse(savedCart));

        const savedPoints = localStorage.getItem('sugarhut_points');
        if (savedPoints) setUserPoints(parseInt(savedPoints));
    }, []);

    // Save cart to localStorage
    useEffect(() => {
        localStorage.setItem('sugarhut_cart', JSON.stringify(cart));
    }, [cart]);

    // Save points to localStorage
    useEffect(() => {
        localStorage.setItem('sugarhut_points', userPoints.toString());
    }, [userPoints]);

    const addToCart = (item) => {
        setCart((prev) => {
            const existing = prev.find((i) => i.id === item.id);
            if (existing) {
                return prev.map((i) =>
                    i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
                );
            }
            return [...prev, { ...item, quantity: 1 }];
        });
        setIsCartOpen(true);
    };

    const removeFromCart = (itemId) => {
        setCart((prev) => prev.filter((i) => i.id !== itemId));
    };

    const updateQuantity = (itemId, delta) => {
        setCart((prev) =>
            prev.map((i) => {
                if (i.id === itemId) {
                    const newQty = Math.max(1, i.quantity + delta);
                    return { ...i, quantity: newQty };
                }
                return i;
            })
        );
    };

    const clearCart = () => {
        setCart([]);
        setAppliedVoucher(null);
    };

    const applyVoucher = (voucher) => {
        setAppliedVoucher(voucher);
    };

    const removeVoucher = () => {
        setAppliedVoucher(null);
    };

    const earnPoints = (amount) => {
        setUserPoints(prev => prev + Math.floor(amount));
    };

    const redeemPoints = (points) => {
        if (userPoints >= points) {
            setUserPoints(prev => prev - points);
            return true;
        }
        return false;
    };

    const cartSubtotal = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);

    let discountAmount = 0;
    if (appliedVoucher) {
        if (appliedVoucher.type === 'percentage') {
            discountAmount = cartSubtotal * (appliedVoucher.discount / 100);
        } else {
            discountAmount = appliedVoucher.discount;
        }
    }

    const cartTotal = Math.max(0, cartSubtotal - discountAmount);
    const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

    return (
        <CartContext.Provider value={{
            cart,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            cartSubtotal,
            cartTotal,
            cartCount,
            discountAmount,
            isCartOpen,
            setIsCartOpen,
            appliedVoucher,
            applyVoucher,
            removeVoucher,
            userPoints,
            earnPoints,
            redeemPoints
        }}>
            {children}
        </CartContext.Provider>
    );
};
